/**
 * ProShip Direct v2 — the OTP step in front of plan activation.
 *
 * Activation starts a recurring charge against a wallet or a credit line, and it is reachable by
 * every panel user with SETTINGS access — including a session someone left open. A second factor on
 * the registered email means the person clicking is the person who holds the account.
 *
 * The code is generated, mailed and checked on the server by ProShip's OTP framework: POST
 * direct/otp sends it, and the paying call carries it with its tokenId (see useActivationOtp.js).
 *
 * What is left here is presentation only: the countdown, the resend throttle, the attempt count
 * the UI shows, the masked address and the merchant-facing messages. newChallenge no longer holds
 * a code anyone checks.
 */

export const OTP_LENGTH = 6;

/** Long enough to find the mail, short enough that a stale code cannot be used later. */
export const OTP_TTL_SECONDS = 600;

/** Cap on guesses. Six digits is a million combinations, but five tries costs an attacker nothing. */
export const MAX_ATTEMPTS = 5;

/** Stops resend being used as a mail bomb against the merchant's own inbox. */
export const RESEND_COOLDOWN_SECONDS = 30;

/**
 * The digits, from the platform CSPRNG rather than Math.random.
 *
 * Math.random is not a cryptographic source and its output is predictable from prior draws. That
 * hardly matters while the code lives in the browser anyway, but the moment this moves server-side
 * the habit should already be right, and a weak generator is the kind of thing nobody revisits.
 */
const digits = (length) => {
  const source = window.crypto?.getRandomValues
    ? Array.from(window.crypto.getRandomValues(new Uint32Array(length)))
    : Array.from({ length }, () => Math.floor(Math.random() * 1e9));
  return source.map((n) => n % 10).join('');
};

/**
 * A fresh challenge for one activation attempt.
 *
 * `attempts` counts wrong guesses, not sends: resending must not hand an attacker five more tries,
 * so resend issues a new code and carries the count forward.
 */
export function newChallenge(email, { now = Date.now(), attempts = 0 } = {}) {
  return {
    code: digits(OTP_LENGTH),
    email: email || '',
    issuedAt: now,
    attempts,
  };
}

export const secondsLeft = (challenge, now = Date.now()) =>
  !challenge ? 0 : Math.max(0, Math.ceil((challenge.issuedAt + OTP_TTL_SECONDS * 1000 - now) / 1000));

export const isExpired = (challenge, now = Date.now()) => secondsLeft(challenge, now) === 0;

export const attemptsLeft = (challenge) =>
  Math.max(0, MAX_ATTEMPTS - (challenge?.attempts || 0));

export const resendIn = (challenge, now = Date.now()) =>
  !challenge
    ? 0
    : Math.max(0, Math.ceil((challenge.issuedAt + RESEND_COOLDOWN_SECONDS * 1000 - now) / 1000));

/**
 * The address, partly hidden.
 *
 * Enough to recognise which inbox to open, not enough to hand a full address to whoever is looking
 * at the screen. A short local part is masked entirely rather than left legible.
 */
export function maskEmail(email) {
  const value = String(email || '').trim();
  const at = value.lastIndexOf('@');
  if (at < 1) return value || 'your registered email';

  const local = value.slice(0, at);
  const domain = value.slice(at);

  if (local.length <= 2) return `${'•'.repeat(local.length)}${domain}`;
  return `${local[0]}${'•'.repeat(Math.min(local.length - 2, 6))}${local[local.length - 1]}${domain}`;
}

/** What to tell the merchant went wrong, per reason. */
export const OTP_MESSAGES = {
  NO_CHALLENGE: 'Send a code first.',
  EXPIRED: 'That code has expired. Send a new one.',
  /*
    Deliberately does NOT offer a resend. Attempts carry forward across resends by design — see
    newChallenge — so a new code would land on the same lockout, and telling them to try one would
    be sending them round a loop. Closing the modal is the real escape, and it says so.
  */
  LOCKED: 'Too many incorrect attempts. Close this window and start again.',
  INCOMPLETE: `Enter all ${OTP_LENGTH} digits.`,
  MISMATCH: 'That code is not right.',
};
